import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Stack, PencilSimple, Trash, Plus, Globe } from "@phosphor-icons/react";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/templates").then((r) => setTemplates(r.data));
  useEffect(() => { load(); }, []);

  const createNew = async () => {
    const { data } = await api.post("/templates", {
      name: "New Report Template", description: "Custom report template",
      sections: [{ id: "sec-1", title: "Section 1", description: "", source_label: "", source_urls: [], use_web_search: false, content: "", order: 0 }],
    });
    toast.success("Template created");
    navigate(`/templates/${data.id}`);
  };

  const remove = async (id, isDefault) => {
    if (isDefault) return toast.error("Default template cannot be deleted");
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/templates/${id}`);
    toast.success("Template deleted"); load();
  };

  return (
    <div className="space-y-8" data-testid="templates-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-[#b45309] bg-[#fef3e2] rounded-full px-3 py-1 inline-block">Report Templates</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mt-3">Templates</h1>
          <p className="text-slate-500 text-base mt-1">Edit sections, text and purple data sources for each template.</p>
        </div>
        <Button data-testid="new-template-btn" onClick={createNew} className="gold-btn rounded-2xl h-12 px-5 font-semibold gap-2">
          <Plus size={18} weight="bold" /> New Template
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {templates.map((t) => {
          const purple = t.sections?.filter((s) => s.source_urls?.length || s.use_web_search).length || 0;
          return (
            <div key={t.id} data-testid={`template-${t.id}`} className="card card-hover p-6 flex flex-col">
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-xl bg-[#e6f0fb] flex items-center justify-center"><Stack size={24} weight="duotone" className="text-[#004182]" /></div>
                {t.is_default && <span className="text-[11px] tag-purple font-medium">Default</span>}
              </div>
              <div className="text-slate-900 font-semibold text-lg mt-4">{t.name}</div>
              <div className="text-slate-500 text-sm mt-1 line-clamp-2 flex-1">{t.description}</div>
              <div className="flex items-center gap-3 text-xs text-slate-400 mt-4">
                <span>{t.sections?.length || 0} sections</span>
                <span className="flex items-center gap-1 text-purple-600"><Globe size={13} /> {purple} scraped</span>
              </div>
              <div className="flex gap-2 mt-5">
                <Button data-testid={`edit-template-${t.id}`} onClick={() => navigate(`/templates/${t.id}`)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl gap-2 h-11"><PencilSimple size={16} /> Edit</Button>
                <Button data-testid={`delete-template-${t.id}`} onClick={() => remove(t.id, t.is_default)}
                  variant="ghost" className="text-slate-400 hover:text-red-500 h-11"><Trash size={16} /></Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
